import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  DataRoomMember,
  DataRoomMembers,
} from 'router/subrouters/DataRoom/models/DataRoomMember';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import { Button } from 'ncoded-component-library';
import AddTeamMemberModal from '../AddTeamMemberModal';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import ManageTeamMemberDrawer from '../ManageTeamMemberDrawer';
import MembersFlatTable from 'router/subrouters/DataRoom/subrouters/Members/pages/Members/components/MembersFlatTable';
import {
  MEMBERS_TABLE_LIMIT,
  MULTI_SORT_DIRECTION_COPY,
  MULTI_SORT_LABELS,
  SORTING_VALUES,
} from 'router/subrouters/DataRoom/subrouters/Members/pages/Members/components/MembersFlatTable/data';
import MultiFilterPanel from 'components/MultiFilterPanel';
import MultiSortPanel from 'components/MultiSortPanel';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import useMultiSort from 'hooks/useMultiSort';
import useTeamMemberFilters from './hooks/useTeamMemberFilters';
import useDeleteMember from 'router/subrouters/DataRoom/hooks/useDeleteMember';
import { DropdownOption } from 'models/Dropdown';
import {
  TEAM_PAGE_PARAM,
  TEAM_SORT_COLUMNS,
  TEAM_SORT_PARAM,
  TEAM_FILTER_PARAMS,
} from './data';
import PlusIcon from 'icons/Plus.icon';
import TagIcon from 'icons/Tag.icon';
import { getArrayLength, getIsArrayEmpty, getFilterOptionByValue } from 'utils';
import useChipLabelClickRefs from 'hooks/useChipLabelClickRefs';

import './TeamMembersTable.styles.scss';
import './TeamMembersTable.styles.responsive.scss';

type TeamMembersTableProps = {
  className?: string;
};

const TeamMembersTable: React.FC<TeamMembersTableProps> = (props) => {
  const { className } = props;

  const [selectedMember, setSelectedMember] = useState<DataRoomMember>();

  const { buySideGroups, setBuySideGroups } = useContext(MembersContext);

  const { dataRoomMembers: buySideMembers, _id: buySideGroupId } =
    buySideGroups?.[0] || {};

  const [modal, modalRef] = useCallbackRef<OverlayRef>();
  const [manageMember, manageMemberRef] = useCallbackRef<SideDrawerRef>(null);

  const classes = classNames('team-members-table', className);

  const { t } = useTranslation();

  const {
    multiFilterRef,
    multiSortRef,
    onChipLabelClick,
    onSortChipLabelClick,
  } = useChipLabelClickRefs();

  const deleteMember = useDeleteMember();

  const { filterOptions, filteredMembers } =
    useTeamMemberFilters(buySideMembers);

  const { multiSortValues, multiSortDirectionTranslation } = useMultiSort(
    TEAM_SORT_COLUMNS,
    SORTING_VALUES,
    MULTI_SORT_LABELS,
    MULTI_SORT_DIRECTION_COPY,
  );

  const columnFilterOptions = useMemo(
    () => ({
      name: {
        options: getFilterOptionByValue(filterOptions, TEAM_FILTER_PARAMS.NAME),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.name],
      },
      files: {
        options: getFilterOptionByValue(
          filterOptions,
          TEAM_FILTER_PARAMS.FILES_ROLE,
        ),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.files],
      },
      qa: {
        options: getFilterOptionByValue(
          filterOptions,
          TEAM_FILTER_PARAMS.QA_ROLE,
        ),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.qa],
      },
    }),
    [filterOptions, multiSortDirectionTranslation],
  );

  const handleOnEdit = useCallback(
    (member: DataRoomMember) => {
      setSelectedMember(member);
      manageMember.setOpen(true);
    },
    [manageMember],
  );

  const updateMember = useCallback(
    (member: DataRoomMember) => {
      setSelectedMember((prev) => ({ ...prev, ...member }));

      setBuySideGroups((prev) =>
        prev.map((group) => {
          if (group.dataRoomMembers?.every(({ _id }) => _id !== member._id)) {
            return group;
          }

          return {
            ...group,
            dataRoomMembers: group.dataRoomMembers.map((m) =>
              m._id !== member._id ? m : member,
            ),
          };
        }),
      );
    },
    [setBuySideGroups],
  );

  const updateDataRoomMembers = useCallback(
    ({ buySideGroups }: DataRoomMembers) => {
      setBuySideGroups(buySideGroups);
    },
    [setBuySideGroups],
  );

  // Deleting from the row menu skips the drawer entirely: the confirmation is
  // the only thing that opens.
  const onDeleteMember = useCallback(
    (member: DataRoomMember) =>
      deleteMember({ member, onSuccess: updateDataRoomMembers }),
    [deleteMember, updateDataRoomMembers],
  );

  // The team is managed from its own drawer, which has no activity view, so the
  // menu offers the drawer and the removal rather than the members page's set.
  const rowActionOptions = useCallback(
    (member: DataRoomMember): DropdownOption[] => [
      {
        label: t('editPermissions'),
        onClick: () => handleOnEdit(member),
      },
      {
        label: t('deleteMember'),
        alert: true,
        onClick: () => onDeleteMember(member),
      },
    ],
    [handleOnEdit, onDeleteMember, t],
  );

  if (getIsArrayEmpty(buySideMembers)) return null;

  return (
    <div className="white-panel team-members-table-wrapper">
      <div className="members-panel__header">
        <div className="members-panel__title">
          <TagIcon />
          <p className="name">{t('yourTeam')}</p>

          <p className="count">
            {t('numOfcolleagues', { count: getArrayLength(buySideMembers) })}
          </p>
        </div>

        <div className="members-panel__actions">
          <Button variant="outline" icon={PlusIcon} onClick={modal?.open}>
            {t('addMember')}
          </Button>
        </div>
      </div>

      <div className="members-panel__toolbar">
        <ActiveFilterSortPills
          onChipLabelClick={onChipLabelClick}
          onSortChipLabelClick={onSortChipLabelClick}
          filterOptions={filterOptions}
          sortOptions={multiSortValues}
          sortParam={TEAM_SORT_PARAM}
        />

        <div className="members-panel__toolbar__actions">
          <MultiFilterPanel ref={multiFilterRef} filters={filterOptions} />

          <MultiSortPanel
            ref={multiSortRef}
            options={multiSortValues}
            sortDirectionTranslations={multiSortDirectionTranslation}
            sortParam={TEAM_SORT_PARAM}
          />
        </div>
      </div>

      <MembersFlatTable
        isBuySide
        className={classes}
        sortParam={TEAM_SORT_PARAM}
        pageParam={TEAM_PAGE_PARAM}
        limit={MEMBERS_TABLE_LIMIT}
        columnFilterOptions={columnFilterOptions}
        members={filteredMembers}
        onRowClick={handleOnEdit}
        onEdit={handleOnEdit}
        rowActionOptions={rowActionOptions}
        selectedMemberId={selectedMember?._id}
        renderDrawers={(prop) => (
          <ManageTeamMemberDrawer
            ref={manageMemberRef}
            member={selectedMember}
            updateMember={updateMember}
            updateDataRoomMembers={updateDataRoomMembers}
            {...prop}
          />
        )}
      />

      <AddTeamMemberModal
        ref={modalRef}
        buySideGroupId={buySideGroupId}
        updateDataRoomMembers={updateDataRoomMembers}
      />
    </div>
  );
};

export default TeamMembersTable;
